import { useAppDispatch, useAppSelector } from 'app/hooks';
import Button from 'components/inputs/button';
import Typeahead from 'components/inputs/typeahead';
import {
  setShouldAutoGenerateLevels,
  setShouldAutoGenerateSpaces
} from 'features/ai';
import {
  setAttomData,
  setPropertyLocation,
  setPropertySummary,
  setWeatherData
} from 'features/project';
import { clearFootprint, setStructure } from 'features/structure';
import { useState } from 'react';
import GooglePlacesSearch from 'react-google-places-autocomplete';
import { Option } from 'react-google-places-autocomplete/build/types';
import { SingleValue } from 'react-select';
import { lookupAddress } from 'utils/property';
import { confirmationModal } from 'utils/ui';

export default function AddressInfo() {
  const dispatch = useAppDispatch();
  const { structure } = useAppSelector((state) => state.structure);
  const { selectedSystems, selectedDuctedSystems } = useAppSelector(
    (state) => state.systems
  );
  const { configurations } = useAppSelector((state) => state.configurations);
  const { attomData, customerAddresses } = useAppSelector(
    (state) => state.project
  );
  const { googleApiLoaded } = useAppSelector((state) => state.ui);
  const { preventAutoGenerateSpaces } = useAppSelector((state) => state.ai);
  const [value, setValue] = useState<SingleValue<Option>>(null);
  const [searchClicked, setSearchClicked] = useState(false);

  const handleAddressTileClick = async (address: string) => {
    setValue({
      label: address,
      value: { description: address }
    } as Option);
    setSearchClicked(true);
    await lookupAddress(address);
    setSearchClicked(false);
  };

  const handleSearchClick = async (val?: SingleValue<Option>) => {
    if (value?.value?.place_id) {
      setSearchClicked(true);
      await lookupAddress(value.label);
      setSearchClicked(false);
    }

    if (val) {
      setSearchClicked(true);
      await lookupAddress(val.label);
      setSearchClicked(false);
    }
  };

  const handleOnChange = (val: SingleValue<Option>) => {
    setValue(val);
    handleSearchClick(val);
  };

  const resetLocation = () => {
    dispatch(setStructure([]));
    dispatch(clearFootprint());
    dispatch(setAttomData(null));
    dispatch(setPropertyLocation(null));
    dispatch(setPropertySummary(null));
    dispatch(setWeatherData(null));
    dispatch(setShouldAutoGenerateLevels(true));
    if (!preventAutoGenerateSpaces) {
      dispatch(setShouldAutoGenerateSpaces(true));
    }
    setValue(null);
  };

  const handleEditClick = () => {
    if (
      structure.length > 0 ||
      configurations.length > 0 ||
      selectedSystems.length > 0 ||
      selectedDuctedSystems.length > 0
    ) {
      confirmationModal(
        'Are you sure you want to change the location? This will reset your structure, configurations, and selected systems!',
        () => {
          resetLocation();
        }
      );
    } else {
      resetLocation();
    }
  };

  return (
    <div className='flex w-full flex-col gap-3'>
      {attomData ? (
        <div className='flex w-full items-center justify-center gap-3'>
          <span className='text-center text-lg font-bold capitalize text-gray-800'>
            {attomData
              ? `${attomData.address.line1?.toLowerCase()}, ${attomData.address.locality?.toLowerCase()}, ${
                  attomData.address.countrySubd
                } ${attomData.address.postal1}`
              : 'Address'}
          </span>
          <Button text='Change' color='secondary' onClick={handleEditClick} />
        </div>
      ) : (
        <>
          <div className='flex w-full items-center justify-center gap-3'>
            <div className='grow'>
              {googleApiLoaded ? (
                <GooglePlacesSearch
                  selectProps={{
                    value,
                    onChange: handleOnChange,
                    placeholder: 'Lookup address...',
                    isClearable: true,
                    className: '[&_input]:!ring-0',
                    classNames: {
                      input: () => '!cursor-text',
                      dropdownIndicator: () => '!cursor-pointer',
                      clearIndicator: () => '!cursor-pointer',
                      option: () => '!cursor-pointer'
                    },
                    onKeyDown: (e) => {
                      if (e.key === 'Enter') {
                        handleSearchClick(value);
                      }
                    }
                  }}
                />
              ) : (
                <div className='text-gray-500'>Loading Google Places…</div>
              )}
            </div>
            <Button
              color='primary'
              text={searchClicked ? 'Searching...' : 'Search'}
              onClick={() => handleSearchClick()}
              loading={searchClicked}
              disabled={!googleApiLoaded || searchClicked}
            />
          </div>
          {customerAddresses.length > 0 && (
            <div className='flex flex-col gap-2'>
              <span className='text-sm font-medium text-gray-600'>
                Or select a customer address on record:
              </span>
              <Typeahead
                options={customerAddresses}
                onChange={(selectedAddress) =>
                  handleAddressTileClick(selectedAddress.toString())
                }
                placeholder='Select an address...'
                disabled={searchClicked}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}
